/**
 * Pure, React-free helpers for the Engineering Change domain: id generation,
 * blank / edit draft factories, multi-value parsing, validation (context-first:
 * why + impact are required), feed sorting, and date formatting.
 *
 * The Change Feed intentionally has NO productivity/volume metric: entries are
 * ordered by recency and read for their why/impact context, never counted or
 * ranked per engineer.
 */

import { v4 as uuid } from 'uuid';
import type {
  EngineeringChange,
  EngineeringChangeDraft,
  EngineeringChangeFilter,
  EngineeringChangeSummary,
} from './types';

/** Deterministic document id for a new change. */
export function changeDocId(): string {
  return `chg_${uuid()}`;
}

/** Creates an empty draft for the log-a-change form (optionally seeding owner). */
export function blankChangeDraft(engineerAlias = ''): EngineeringChangeDraft {
  return {
    title: '',
    engineer: engineerAlias,
    changed: '',
    why: '',
    impact: '',
    relatedDocIds: [],
    relatedStoryIds: [],
    goalId: undefined,
    workstreamId: undefined,
  };
}

/** Projects an existing change into an editable draft. */
export function draftFromChange(change: EngineeringChange): EngineeringChangeDraft {
  return {
    title: change.title,
    engineer: change.engineer,
    changed: change.changed,
    why: change.why,
    impact: change.impact,
    relatedDocIds: change.relatedDocIds ?? [],
    relatedStoryIds: change.relatedStoryIds ?? [],
    goalId: change.goalId,
    workstreamId: change.workstreamId,
  };
}

/**
 * Parses a comma / newline separated string into a normalized, de-duped list
 * (trimmed, empties dropped, original order preserved).
 */
export function parseList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[,\n]/)) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Joins a list back into a comma-separated string for an input field. */
export function listToInput(list?: string[]): string {
  return (list ?? []).join(', ');
}

/**
 * Validates a draft for save. Context-first: a change with no why/impact is not
 * worth feeding to the team, so both are required alongside a title.
 */
export function validateChangeDraft(draft: EngineeringChangeDraft): string[] {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push('A change title is required.');
  if (!draft.engineer.trim()) problems.push('An engineer is required.');
  if (!draft.why.trim()) problems.push('A "why" is required — context is the point of the feed.');
  if (!draft.impact.trim()) problems.push('An "impact" is required — describe who/what is affected.');
  return problems;
}

/** Applies an engineer filter (empty field matches everything). */
export function filterChanges(
  items: EngineeringChangeSummary[],
  filter: EngineeringChangeFilter,
): EngineeringChangeSummary[] {
  const engineer = filter.engineer.trim();
  return items.filter((c) => {
    if (engineer && c.engineer !== engineer) return false;
    return true;
  });
}

/** Unique, sorted engineer aliases present across the given changes. */
export function collectEngineers(items: EngineeringChangeSummary[]): string[] {
  const set = new Set<string>();
  for (const c of items) {
    const t = (c.engineer ?? '').trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Sorts changes newest-first for the feed. */
export function sortByRecency(
  items: EngineeringChangeSummary[],
): EngineeringChangeSummary[] {
  return items
    .slice()
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

/** Formats an ISO timestamp as a compact local date + time for the feed. */
export function formatDateTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
